import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { Typography, Checkbox, Button } from '@percent/lemonade'
import styles from './SmartApplyVariantA.module.scss'
import { useIntroModalState } from '@percent/cause-dashboard/common/hooks'
import { useSmartApplyTracking } from '@percent/cause-dashboard/common/hooks/useSmartApplyTracking'
import { usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { useMetaOfferAllowedInCountry } from '@percent/cause-dashboard/common/hooks/useMetaOfferAllowedInCountry/useMetaOfferAllowedInCountry'
import { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'
import { SMART_APPLY_PARTNER_LOGOS, ADOBE_SUB_PRODUCT_LOGOS } from '../smartApplyPartnerLogos/SmartApplyPartnerLogos'

type Product = {
  name: string
  displayName: string
  description: string
  logo: string
  checked: boolean
}

// Convert camelCase to kebab-case for test IDs
const toKebabCase = (str: string) => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

type SmartApplyVariantAProps = {
  hasCanvaApplication: boolean
  setSelectedPartner: (partner: SmartApplyPartner) => void
  appliedPartners?: Set<SmartApplyPartner>
  page?: number | null
  pagesCount?: number
  secondaryAction?: () => void
  onClose: () => void
  registerDismissCallback?: (callback: (() => void) | null) => void
}

export function SmartApplyVariantA({
  hasCanvaApplication,
  setSelectedPartner,
  appliedPartners = new Set(),
  page = null,
  secondaryAction,
  pagesCount,
  onClose,
  registerDismissCallback
}: Readonly<SmartApplyVariantAProps>) {
  const { t } = useTranslation()
  const { recordSmartApplyAction } = useIntroModalState()
  const isLovableSmartApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_LOVABLE_SMART_APPLY)
  const isClaudeSmartApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_CLAUDE_SMART_APPLY)
  const isMetaSmartApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY)
  const isMetaAllowedInCountry = useMetaOfferAllowedInCountry(isMetaSmartApplyEnabled)
  const shouldShowMeta = isMetaSmartApplyEnabled && isMetaAllowedInCountry

  const { trackProductApplied, trackDismissed } = useSmartApplyTracking({ variant: 'variant_a', page })

  useEffect(() => {
    registerDismissCallback?.(() => {
      trackDismissed({ appliedProductsCount: appliedPartners.size })
    })
    return () => registerDismissCallback?.(null)
  }, [registerDismissCallback, trackDismissed, appliedPartners.size])

  const isEligibleForAdobeExpress = !hasCanvaApplication

  const initialProducts = useMemo<Product[]>(
    () =>
      [
        {
          name: 'adobe',
          displayName: '', // Will be handled specially in render
          description: '', // Will be handled specially in render
          logo: SMART_APPLY_PARTNER_LOGOS.adobe,
          checked: appliedPartners.has('adobe')
        },
        {
          name: 'openai',
          displayName: t('typography.smartApply.openai.title'),
          description: t('typography.smartApply.openai.description'),
          logo: SMART_APPLY_PARTNER_LOGOS.openai,
          checked: appliedPartners.has('openai')
        },
        {
          name: 'zoom',
          displayName: t('typography.smartApply.zoom.title'),
          description: t('typography.smartApply.zoom.description'),
          logo: SMART_APPLY_PARTNER_LOGOS.zoom,
          checked: appliedPartners.has('zoom')
        },
        ...(shouldShowMeta
          ? [
              {
                name: 'meta',
                displayName: t('typography.smartApply.meta.title'),
                description: t('typography.smartApply.meta.description'),
                logo: SMART_APPLY_PARTNER_LOGOS.meta,
                checked: appliedPartners.has('meta')
              }
            ]
          : []),
        ...(isLovableSmartApplyEnabled
          ? [
              {
                name: 'lovable',
                displayName: t('typography.smartApply.lovable.title'),
                description: t('typography.smartApply.lovable.description'),
                logo: SMART_APPLY_PARTNER_LOGOS.lovable,
                checked: appliedPartners.has('lovable')
              }
            ]
          : []),
        ...(isClaudeSmartApplyEnabled
          ? [
              {
                name: 'claude',
                displayName: t('typography.smartApply.claude.title'),
                description: t('typography.smartApply.claude.description'),
                logo: SMART_APPLY_PARTNER_LOGOS.claude,
                checked: appliedPartners.has('claude')
              }
            ]
          : [])
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [t, appliedPartners, shouldShowMeta, isLovableSmartApplyEnabled, isClaudeSmartApplyEnabled]
  )

  const [products, setProducts] = useState<Product[]>(initialProducts)

  useEffect(() => {
    setProducts(previousProducts => {
      const checkedNames = new Set(previousProducts.filter(product => product.checked).map(product => product.name))

      return initialProducts.map(product => ({
        ...product,
        checked: product.checked || checkedNames.has(product.name)
      }))
    })
  }, [initialProducts])

  const shouldShowPageIndicator = !!(page && pagesCount && pagesCount > 1)

  // Calculate button text based on applied partners
  const getButtonText = () => {
    if (appliedPartners.size === 0) return t('typography.smartApply.notInterested')

    const closeButtonText =
      !page || page === pagesCount ? t('button.close') : t('offersModal.smartApplyPage.submitted.cta')

    if (appliedPartners.size === products.length) return closeButtonText

    return t('typography.smartApply.skipRemaining')
  }

  const handleProductToggle = (productName: string) => {
    setProducts(prev => prev.map(p => (p.name === productName ? { ...p, checked: !p.checked } : p)))
    setSelectedPartner(productName as SmartApplyPartner)
    trackProductApplied({ productName })
  }

  const handleClick = secondaryAction
    ? () => {
        recordSmartApplyAction('dismissed')
        trackDismissed({ appliedProductsCount: appliedPartners.size })
        secondaryAction()
      }
    : () => {
        recordSmartApplyAction(appliedPartners.size > 0 ? 'applied' : 'dismissed')
        trackDismissed({ appliedProductsCount: appliedPartners.size })
        onClose()
      }

  // Common event handlers for product cards
  const createCardHandlers = (productName: string, isApplied: boolean) => ({
    onClick: (e: React.MouseEvent) => {
      if (isApplied || (e.target as HTMLElement).closest('input[type="checkbox"]')) return
      handleProductToggle(productName)
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (isApplied || (e.key !== 'Enter' && e.key !== ' ')) return
      e.preventDefault()
      handleProductToggle(productName)
    }
  })

  // Render checkbox or apply button based on checked state
  const renderActionElement = (product: Product) => {
    if (!product.checked) {
      return (
        <Typography variant="default" className={styles.clickToApply}>
          {t('button.apply')}
        </Typography>
      )
    }
    return (
      <Checkbox
        name={product.name}
        variant="default"
        label=""
        onChange={() => handleProductToggle(product.name)}
        active={product.checked}
        dataTestId={`${toKebabCase(product.name)}-checkbox`}
        className={styles.productCheckbox}
      />
    )
  }

  return (
    <div className={styles.smartApplyContainer}>
      <div className={styles.contentWrapper}>
        {shouldShowPageIndicator && (
          <p className={styles.pageIndicator}>
            {page} {t('offersModal.page.of')} {pagesCount}
          </p>
        )}
        <Typography className={styles.title} variant="h2">
          <span role="img" aria-label="sparkles">
            ✨
          </span>{' '}
          {t('typography.smartApply.title')}
        </Typography>

        <div className={styles.subtitles}>
          <Typography variant="large">
            <Box
              dangerouslySetInnerHTML={{
                __html: t('typography.smartApply.subtitle1')
              }}
            />
          </Typography>
        </div>

        <div className={styles.checkboxesContainer}>
          {products.map(product => {
            // Special handling for Adobe card
            if (product.name === 'adobe') {
              const isApplied = appliedPartners.has('adobe')
              const handlers = createCardHandlers(product.name, isApplied)
              return (
                <button
                  type="button"
                  key={product.name}
                  className={`${styles.productCard} ${styles.adobeCard} ${isApplied ? styles.appliedCard : ''}`}
                  disabled={isApplied}
                  {...handlers}
                >
                  <div className={styles.adobeProductsContainer}>
                    <div className={styles.adobeProduct}>
                      <img
                        src={ADOBE_SUB_PRODUCT_LOGOS.adobeAcrobat}
                        alt={t('typography.smartApply.adobeAcrobatPro.title')}
                        className={styles.productLogo}
                      />
                      <div className={styles.adobeProductInfo}>
                        <Typography variant="default-bold" className={styles.productTitle}>
                          {t('typography.smartApply.adobeAcrobatPro.title')}
                        </Typography>
                        <Typography $color="gray300" variant="default" className={styles.productDescription}>
                          {t('typography.smartApply.adobeAcrobatPro.description')}
                        </Typography>
                      </div>
                    </div>
                    {isEligibleForAdobeExpress && (
                      <div className={styles.adobeProduct}>
                        <img
                          src={ADOBE_SUB_PRODUCT_LOGOS.adobeExpress}
                          alt={t('typography.smartApply.adobeExpress.title')}
                          className={styles.productLogo}
                        />
                        <div className={styles.adobeProductInfo}>
                          <Typography variant="default-bold" className={styles.productTitle}>
                            {t('typography.smartApply.adobeExpress.title')}
                          </Typography>
                          <Typography $color="gray300" variant="default" className={styles.productDescription}>
                            {t('typography.smartApply.adobeExpress.description')}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                  {renderActionElement(product)}
                </button>
              )
            }

            // Regular product cards
            const isApplied = appliedPartners.has(product.name as SmartApplyPartner)
            const handlers = createCardHandlers(product.name, isApplied)
            return (
              <button
                type="button"
                key={product.name}
                className={`${styles.productCard} ${isApplied ? styles.appliedCard : ''}`}
                disabled={isApplied}
                {...handlers}
              >
                <img src={product.logo} alt={product.displayName} className={styles.productLogo} />
                <div className={styles.productInfo}>
                  <Typography variant="default-bold" className={styles.productTitle}>
                    {product.displayName}
                  </Typography>
                  <Typography $color="gray300" variant="default" className={styles.productDescription}>
                    {product.description}
                  </Typography>
                </div>
                {renderActionElement(product)}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`${styles.buttonWrapper} ${styles.applyButtonWrapper}`}>
        <Button variant="tertiary" stretch onClick={handleClick} data-testid="smart-apply-skip-button">
          {getButtonText()}
        </Button>
      </div>
    </div>
  )
}
